(() => {
    class Resources {
        constructor(Component) { this.Component = Component }
        get js()    { return this._js || (this._js = this.Component.read('resources-js').split(',') || []) }
        get css()   { return this._css || (this._css = this.Component.read('resources-css').split(',') || []) }
        get available() { return this.js.length || this.css.length }
        get lazyLoading() {
            return this.Component.container.classList !== undefined &&
                this.Component.container.classList.contains('js-component-resources-lazyloading');
        }
    }

    class Component {
        constructor(container) {
            this.ready      = new Promise(resolve => this.isReadyToResolve = resolve);
            this.container  = container;
            this.containers = {};
            // prevent default
            this.prevent    = false;
            // collect promises
            this.promises   = [];
            // this "True" can only be read on plain JS container
            this.Resources  = new Resources(this);
            this.addResources();
            // Retrieve Component
            this.container.getComponent = component => this.container[component || 'Instance'];
            this.updateInterval = {
                ms: 1,
            };
        }

        get Observer()  { return this._observer || (this._observer = new App.Entities.Observer(this)) }
        get $()         { return this._$ || (this._$ = $(this.container)) }

        isReady() {
            return new Promise(resolve => {
                const interval = App.setInterval(() => {
                    if (this.promises.length === 0) {
                        resolve(this);
                        clearInterval(interval);
                    }
                }, this.updateInterval.ms);
            });
        }

        /**
         * Add a render blocking promise, like a API call, template or translation
         * This will hold on the update method until this all promises are resolved
         *
         * @param promise
         * @return {Component}
         */
        addRenderBlockingPromise(promise) {
            this.promises.push(promise);
            return this;
        }
        addRenderBlockingPromises(promises = []) {
            promises.forEach(p => this.addRenderBlockingPromise(p));
            return this;
        }

        setContainer(name, selector) {
            this.containers[name] = this.container.querySelector(selector);
            return this;
        }

        getContainer(name) {
            return this.containers[name];
        }

        /**
         * This read a data attr from the component set in PHP by `setData`
         * @param name
         * @param hide True if we want to reduce html after rendering
         * @returns {string|*}
         */
        read(name, hide = true) {
            let camelize = function(str) {
                return str.replace(/\W+(.)/g, function(match, chr) {
                    return chr.toUpperCase();
                });
            };
            // return empty string is it doesn't exists
            if (this.container.dataset === undefined || this.container.dataset[camelize(name)] === undefined) return '';
            let value = this.container.dataset[camelize(name)];
            if (hide) this.container.removeAttribute('data-' + name);
            return value;
        }

        preventDefault(prevent = true) {
            this.prevent = prevent;
            return this;
        }

        addResources() {
            if (this.Resources.available) {
                if (this.Resources.lazyLoading) {
                    const o = new IntersectionObserver(([e], o) => {
                        if (e.intersectionRatio) {
                            this.handleResources();
                            o.unobserve(e.target);
                        }
                    });
                    o.observe(this.container);
                } else {
                    this.handleResources();
                }
            }
        }

        handleResources() {
            return new Promise(resolve => {
                Promise.all([
                    ...this.addJsResources(this.Resources.js),
                    ...this.addCssResources(this.Resources.css),
                ]).then(() => {
                    resolve();
                });
            });
        }

        addJsResources(files) {
            const promises = [];
            files.forEach((file) => {
                if (file) promises.push(App.Page.getScript(file));
            });
            return promises;
        }

        addCssResources(files) {
            const promises = [];
            files.forEach((file) => {
                if (file) {
                    // Wait for CSS to load
                    this.addRenderBlockingPromise(App.Interface.Resources.css(file));
                    promises.push(App.Page.getStyle(file));
                }
            });
            return promises;
        }

        on(selector, handler, event = 'click') {
            this.container.addEventListener(event, function(e) {
                // loop parent nodes from the target to the delegation node
                for (let target = e.target; target && target != this; target = target.parentNode) {
                    if (target.matches(selector)) {
                        handler.call(target, e);
                        break;
                    }
                }
            }, false);
        }

        /**
         * Debounce
         * Use of this super is optional, it checks if there are any promises listed and update when all resolve.
         * On the contrary, this method only sets a timeout to avoid triggering many updates on the same time
         *
         * @param callback
         * @param timeout
         * @return {Component}
         */
        async update(callback = false, timeout = this.updateInterval.ms) {
            if (this.promises.length) {
                Promise.all(this.promises).then(async (values) => {
                    if (typeof callback === 'function') await callback(values);
                    this.isReadyToResolve();
                    this.promises = [];
                });
            } else {
                /* Kill old interval */
                if (this.updateTimeOut)
                    clearTimeout(this.updateTimeOut);
                /* Create new interval */
                this.updateTimeOut = setTimeout(async () => {
                    if (typeof callback === 'function') await callback();
                    this.isReadyToResolve();
                }, timeout);
            }
            return this;
        }
    }
    App.Component = Component;
})();
